import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore'
import { firebaseDb } from './init'

const studentInteractivePapersCollectionName = 'studentInteractivePapers'

const studentInteractivePapersCollectionRef = collection(
  firebaseDb,
  studentInteractivePapersCollectionName,
)

const studentInteractivePapersDocRef = (id: string) =>
  doc(firebaseDb, studentInteractivePapersCollectionName, id)

export const migrateInteractivePapersFromAnonymous = async (
  anonymousUserId: string,
  userId: string,
) => {
  try {
    // First, find all papers associated with the anonymousUserId
    const paperQuery = query(
      studentInteractivePapersCollectionRef,
      where('anonymousUserId', '==', anonymousUserId),
    )

    const papersSnapshot = await getDocs(paperQuery)

    if (papersSnapshot.empty) {
      console.debug(
        '[Student Interactive Papers Migration]: No papers to migrate for this user',
      )
      return
    }

    console.debug(
      `[Student Interactive Papers Migration]: Found ${papersSnapshot.size} papers to migrate`,
    )

    // Update each paper to add the userId field
    const batch = writeBatch(firebaseDb)

    // Keep track of topics to migrate
    const topicIds: string[] = []

    papersSnapshot.docs.forEach((paperDoc) => {
      const paperRef = studentInteractivePapersDocRef(paperDoc.id)
      batch.update(paperRef, {
        userId: userId,
        updatedAt: serverTimestamp(),
      })

      // Store the topic ID for migration
      const topicId = paperDoc.data().topicId
      if (topicId) {
        topicIds.push(topicId)
      }
    })

    // Commit the batch for papers
    await batch.commit()

    // Now update all the topics
    if (topicIds.length > 0) {
      const topicsBatch = writeBatch(firebaseDb)

      for (const topicId of topicIds) {
        const topicRef = doc(firebaseDb, 'topics', topicId)
        topicsBatch.update(topicRef, {
          userId: userId,
          updatedAt: serverTimestamp(),
        })
      }

      await topicsBatch.commit()
      console.debug(
        `[Student Interactive Papers Migration]: Migrated ${topicIds.length} topics to authenticated user`,
      )
    }

    // Finally, update all lessons associated with these topics
    for (const topicId of topicIds) {
      // Find lessons for this topic
      const lessonsQuery = query(
        collection(firebaseDb, 'lessons'),
        where('topicId', '==', topicId),
        where('anonymousUserId', '==', anonymousUserId),
      )

      const lessonsSnapshot = await getDocs(lessonsQuery)

      if (!lessonsSnapshot.empty) {
        const lessonsBatch = writeBatch(firebaseDb)

        lessonsSnapshot.docs.forEach((lessonDoc) => {
          const lessonRef = doc(firebaseDb, 'lessons', lessonDoc.id)
          lessonsBatch.update(lessonRef, {
            userId: userId,
            updatedAt: serverTimestamp(),
          })
        })

        await lessonsBatch.commit()
        console.debug(
          `[Student Interactive Papers Migration]: Migrated ${lessonsSnapshot.size} lessons for topic ${topicId}`,
        )
      }
    }

    console.debug(
      '[Student Interactive Papers Migration]: Successfully migrated papers, topics, and lessons to authenticated user',
    )
  } catch (error) {
    console.error('[Student Interactive Papers Migration] Error: ', error)
    throw error // Re-throw to be handled by the caller
  }
}
